import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { ScrambleText } from './ScrambleText';

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

const HERO_STATS = [
  { label: 'ACTIVE_SINCE', value: '2019' },
  { label: 'IDENTITY', value: 'REDACTED' },
  { label: 'PRIVACY_STACK', value: 'XMR / ZEC' },
  { label: 'ON_CHAIN_STATUS', value: 'NOT YET' },
];

function goTo(id) {
  document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
}

function HeroTagline() {
  return (
    <>
      <motion.div variants={itemVariants} className="max-w-2xl mt-6">
        <p className="hero-subtitle" data-testid="hero-subtitle">
          <span className="text-[#FFC700]">anonymous by design</span><br />
          privacy. sovereignty. private money.
        </p>
        <p className="body-text text-white/50 mt-3 max-w-xl">
          Art, objects and ideas from outside the ledger.
        </p>
      </motion.div>

      <motion.div variants={itemVariants} className="mt-8 max-w-xl">
        <p className="manifesto-line" data-testid="hero-manifesto">
          "The object exists. The identity does not.<br />
          Identity is a liability. Cryptography is the signature."
        </p>
      </motion.div>
    </>
  );
}

function HeroCTAs() {
  return (
    <motion.div variants={itemVariants} className="mt-12 flex flex-row gap-3 sm:gap-4">
      <a
        href="#nakamoto"
        onClick={(e) => { e.preventDefault(); goTo('#nakamoto'); }}
        className="btn-primary hero-cta flex-1 sm:flex-initial justify-center text-center"
        data-testid="hero-cta-archive"
      >
        ENTER THE ARCHIVE
      </a>
      <a
        href="#about"
        onClick={(e) => { e.preventDefault(); goTo('#about'); }}
        className="btn-ghost hero-cta flex-1 sm:flex-initial justify-center text-center"
        data-testid="hero-cta-about"
      >
        WHO IS ZATO?
      </a>
    </motion.div>
  );
}

function HeroStats() {
  return (
    <motion.div
      variants={itemVariants}
      className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-xl"
      data-testid="hero-stats"
    >
      {HERO_STATS.map((stat) => (
        <div key={stat.label} className="hero-stat-panel">
          <span className="hero-stat-label">{stat.label}</span>
          <span className="hero-stat-value">{stat.value}</span>
        </div>
      ))}
    </motion.div>
  );
}

export function HeroSection() {
  return (
    <section id="hero" className="relative min-h-screen flex flex-col justify-start overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-[#050505]" />
      <div className="absolute inset-0 hex-grid-bg opacity-5" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 max-w-7xl mx-auto px-6 pt-40 sm:pt-44 pb-28"
      >
        <motion.div variants={itemVariants} className="mb-4">
          <span className="mono-tag">// IDENTITY: [REDACTED] _ NETWORK: ENCRYPTED</span>
        </motion.div>

        <motion.h1 variants={itemVariants} className="hero-title" data-testid="hero-title">
          <ScrambleText text="ZATO" trigger="mount" as="span" />
        </motion.h1>

        <HeroTagline />
        <HeroCTAs />
        <HeroStats />
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        onClick={() => goTo('#about')}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 text-white/30 hover:text-[#FFC700] transition-colors duration-200 animate-bounce"
        data-testid="hero-scroll-down"
      >
        <ChevronDown size={24} />
      </motion.button>
    </section>
  );
}
